import { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Star } from "./Logo";
import { useLang } from "@/i18n/LanguageContext";

export const ScreenLightbox = ({ screens, captions, idx, setIdx }) => {
  const { lang } = useLang();
  const step = useCallback((d) => setIdx((i) => (i === null ? i : (i + d + screens.length) % screens.length)), [screens.length, setIdx]);

  useEffect(() => {
    if (idx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIdx(null);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [idx, step, setIdx]);

  return (
    <AnimatePresence>
      {idx !== null && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(28,22,18,0.88)", backdropFilter: "blur(14px)" }}
          onClick={() => setIdx(null)}
          data-testid="app-screen-lightbox"
        >
          <button
            type="button"
            aria-label="Close"
            data-testid="lightbox-close"
            onClick={() => setIdx(null)}
            className="absolute right-5 top-5 rounded-full border border-white/25 p-2.5 text-white/90 transition-colors duration-300 hover:bg-white/10"
          >
            <X size={20} />
          </button>
          <button
            type="button"
            aria-label="Previous"
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); step(-1); }}
            className="absolute left-2 sm:left-6 rounded-full border border-white/25 p-2.5 text-white/90 transition-colors duration-300 hover:bg-white/10"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            aria-label="Next"
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); step(1); }}
            className="absolute right-2 sm:right-6 rounded-full border border-white/25 p-2.5 text-white/90 transition-colors duration-300 hover:bg-white/10"
          >
            <ChevronRight size={22} />
          </button>
          <motion.figure
            key={screens[idx]}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="flex max-h-full flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`/app-screens/${lang}/${screens[idx]}.webp?v=5`}
              alt={captions[screens[idx]]}
              className="max-h-[78vh] w-auto rounded-[28px] border-[6px] shadow-2xl"
              style={{ borderColor: "var(--on-surface)", background: "var(--on-surface)" }}
              data-testid="lightbox-image"
            />
            <figcaption className="mt-4 flex items-center gap-2 text-center text-sm text-white/85" data-testid="lightbox-caption">
              <Star size={11} style={{ color: "var(--gold)" }} /> {captions[screens[idx]]}
              <span className="text-white/45">· {idx + 1}/{screens.length}</span>
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
